import { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Grid,
  Breadcrumbs,
  Typography,
  Button,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Table,
  IconButton,
  Stack,
  Switch
} from '@mui/material';
import { Add, Home as HomeIcon } from '@mui/icons-material';
import PageTitle from '../../../common/PageTitle';
import LinkNavegacao from '../../../common/Link';
import { useCommonItems } from '../../../../contexts/CommonItensProvider';
import { ClockIcon, EditIcon, TrashIcon } from '../../../common/Icons';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { checkPermissionsAndRedirect, hasPermission } from '../../../../configs/functions';
import { EmptyTableRow } from '../../../common/EmptyTableRow';
import { SkeletonTableRow } from '../../../common/SkeletonTableRow';
import { listaEmpresas } from '../companies/configs/functions';
import { Pagination } from '../../../common/Pagination';
import {
  listaRegimesTrabalho,
  deletaRegimeTrabalho,
  atualizaRegimeTrabalho,
  atualizaDepartamento, deletaDepartamento
} from './configs/functions';
import { SelectEmpresaFilter } from '../../../common/SelectEmpresaFilter';
import { useAppContext } from '../../../../contexts/AppContext';
import { WorkRegimeForm } from './WorkRegimeForm';
import { CustomToolbarExportTable } from '../../../common/CustomToolbarExportTable';
import { extractErrorMessage } from '../../../../utils/extract-error-message';
import {FiltersButton} from "../../../common/FiltersButton";
import {ItemsTableFilter} from "./components/ItemsTableFilter";
import {ItemsNoCodeTableFilter} from "./components/ItemsNoCodeTableFilter";

const WorkRegimeRow = ({ data, actionsAllowed, handleExibirAlerta, handleOpenEditForm, handleUpdateStatus, updateData }) => {
  const [active, setActive] = useState(data.status);
  const [openDialog, setOpenDialog] = useState(false);

  async function handleToggleStatus() {
    if (!hasPermission(["admin", "admin_regimeTrabalhos", "update_regimeTrabalhos"])) {
      handleExibirAlerta('Erro', 'Você não tem permissão para executar esta ação.', 'error')
      return false;
    }

    try {
      const res = await atualizaRegimeTrabalho(data.id, { status: !active })
      if (res) {
        const message = active ? 'Regime de trabalho inativado com sucesso' : 'Regime de trabalho ativado com sucesso'
        handleExibirAlerta('Sucesso', message, 'success')
        setActive(!active)
        handleUpdateStatus(data.id, !data.status)
      }
    } catch (error) {
      handleExibirAlerta('Erro', 'Erro ao alterar status', 'error')
      console.log(error)
    }
  }

  const deleteRegime = () => {
    const id = data.id;
    deletaRegimeTrabalho(id)
        .then((response) => {
          const message = response.data.message ?? 'Regime de trabalho excluído com sucesso'
          handleExibirAlerta('Sucesso', message, 'success');
          handleUpdateStatus(id, false);
          setActive(false);
        }).catch((error) => {
      const message = error.response.data.message ?? 'Erro ao executar operação'
      handleExibirAlerta('Erro', message, 'error');
    }).finally(() => {
      if (updateData) updateData();
    })

    setOpenDialog(false)
  }
  return (
    <>
      <TableRow key={data.id}>
        <TableCell>{data.titulo}</TableCell>
        <TableCell>{data.codExterno}</TableCell>
        <TableCell>{data.descricao}</TableCell>
        <TableCell>
          <Switch
            checked={active}
            onChange={handleToggleStatus}
            disabled={!hasPermission(["admin", "admin_regimeTrabalhos", "update_regimeTrabalhos"])}
          />
        </TableCell>
        <TableCell align='right' sx={{ minWidth: 110 }}>
          {hasPermission(["admin", "admin_regimeTrabalhos", "update_regimeTrabalhos"]) && (
            <IconButton aria-label="Editar" onClick={() => handleOpenEditForm(data)}>
              <EditIcon />
            </IconButton>
          )}
          {hasPermission(["admin", "admin_regimeTrabalhos", "delete_regimeTrabalhos"]) && (
            <IconButton aria-label="Excluir" onClick={() => setOpenDialog(true)}>
              <TrashIcon />
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      <ConfirmDialog
          description={'Tem certeza que deseja excluir este Regime de trabalho?'}
          title={`Excluir Regime de trabalho ${data.nome}`}
          goAction={deleteRegime}
          handleClose={setOpenDialog}
          state={openDialog}
      />
    </>
  );
};

export function ListOfWorkRegimes() {
  const { exibirAlerta } = useCommonItems();
  const { getEmpresaIdSession, setEmpresaIdSession } = useAppContext();

  // estados para controle de paginacao
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [numTotalItems, setNumTotalItems] = useState(0);

  const [regimes, setRegimes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresaId, setSelectedEmpresaId] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [selectedRegime, setSelectedRegime] = useState(null);

  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamPage = searchParams.get('page');
  const searchParamPerPage = searchParams.get('perPage');

  const columns = useMemo(() => [
    { field: 'titulo', headerName: 'Regime de Trabalho' },
    { field: 'codExterno', headerName: 'Cód. Externo' },
    { field: 'descricao', headerName: 'Descrição' },
    { field: 'empresa.nome', headerName: 'Empresa', valueGetter: (params) => params.row.empresa?.nome },
    { field: 'status', headerName: 'Status', valueGetter: (params) => params.row.status === 1 ? 'Ativo' : 'Inativo' },
  ], []);

  const { data: empresasData, error: listaEmpresasError } = useQuery({
    queryKey: ['listaEmpresas'],
    queryFn: async () => {
      const response = await listaEmpresas(null, null, null, true);
      const empresasData = response.data.data;

      const selectedEmpresaId = getEmpresaIdSession() || empresasData[0]?.id;

      setEmpresaIdSession(selectedEmpresaId);

      setSearchParams((state) => {
        state.set('id_empresa', String(selectedEmpresaId));
        state.set('page', String(page + 1));
        state.set('perPage', String(rowsPerPage));

        return state;
      });
      return response;
    },
    refetchOnWindowFocus: false,
  });

  const queryFilters = {
    id_empresas: Array.isArray(searchParams.get('id_empresa')) ? searchParams.get('id_empresa') : [searchParams.get('id_empresa')],
    titulo: searchParams.get('nome'),
    limit: rowsPerPage,
    offset: page * rowsPerPage,
  };

  const queryKey = ['listaRegimesTrabalho', queryFilters];
  const { data: listaRegimesTrabalhoData, isLoading, error: listaRegimesTrabalhoError, refetch } = useQuery({
    queryKey,
    queryFn: () => listaRegimesTrabalho(queryFilters),
    enabled: !!searchParamPage && !!searchParamPerPage,
  });

  const updateRegimeStatus = (categoryId, status) => setRegimes(
      regimes.map((item) => {
        if (item.id === categoryId) {
          item.status = status;
        }
        return item;
      })
  );

  function handleEdiRegime(item) {
    setSelectedRegime(item);
    setOpenForm(true);
  }

  useEffect(() => {
    if (listaEmpresasError) {
      exibirAlerta('Erro', 'Erro ao carregar empresas', 'error');
    }

    if (listaRegimesTrabalhoError) {
      exibirAlerta('Erro', 'Erro ao carregar regimes de trabalho', 'error');
    }
  }, [exibirAlerta, listaEmpresasError, listaRegimesTrabalhoError])

  useEffect(() => {
    if (listaRegimesTrabalhoData) {
      setRegimes(listaRegimesTrabalhoData.data.regimes);
      setNumTotalItems(listaRegimesTrabalhoData.data.numero_total);
      setSelectedEmpresaId(searchParams.get('id_empresa'));
      setEmpresas(empresasData.data.data)
    }
  }, [listaRegimesTrabalhoData]);

  useEffect(() => {
    if (openForm) return;
    setSelectedRegime(null);

    const queryFilters = { ...Object.fromEntries(searchParams) }

    const tableFilters = { ...queryFilters };
    delete tableFilters.page;
    delete tableFilters.perPage;

    const activeFilters = Object.keys(tableFilters).length;

    setActiveFilters(activeFilters);
    setIsOpenFilters(activeFilters > 0);

  }, [openForm, searchParams]);

  useEffect(() => {
    const requiredPermissionsView = ["admin", "admin_regimeTrabalhos", "list_regimeTrabalhos"];
    checkPermissionsAndRedirect(requiredPermissionsView);
  }, []);

  return (
    <>
      <Helmet title="Regimes de Trabalho" defer={false} />

      <Grid container direction="row" justifyContent="flex-start" alignItems="stretch" spacing={3}>
        <Grid item xs={12}>
          <Breadcrumbs>
            <LinkNavegacao to="/">
              <HomeIcon fontSize="small" />
            </LinkNavegacao>
            <Typography variant="span">Gerenciamento</Typography>
            <LinkNavegacao to="/gerenciamento/organizacao">Organização</LinkNavegacao>
            <Typography variant="span">Regimes de Trabalho</Typography>
          </Breadcrumbs>
          <PageTitle
            icon={<ClockIcon fontSize="large" />}
            title="Regimes de Trabalho"
            description="Administre os regimes de trabalho das empresas."
          />
        </Grid>

        <Grid item xs={12}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <FiltersButton
                isOpenFilters={isOpenFilters}
                setIsOpenFilters={setIsOpenFilters}
                activeFilters={activeFilters}
            />

            {hasPermission(['admin', 'admin_regimeTrabalhos', 'create_regimeTrabalhos']) && (
              <Grid item xs={12} md={4}>
                <Typography sx={{ textAlign: { xs: 'center', md: 'right' } }}>
                  <Button variant="contained" startIcon={<Add />} onClick={() => setOpenForm(true)}>
                    Cadastrar Regime de Trabalho
                  </Button>
                </Typography>
              </Grid>
            )}
          </Stack>
        </Grid>

        <ItemsTableFilter
            isOpenFilters={isOpenFilters}
            isLoadingList={isLoading}
            empresas={empresasData?.data?.data || []}
            item={"Regime de trabalho"}
        />

        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <CustomToolbarExportTable
              rows={regimes}
              columns={columns}
              reportName="relatorio_regime_trabalho"
              reportPdfTitle="RELATÓRIO DE REGIME DE TRABALHO"
              getDataWithoutPaginate={() => {
                const { limit, offset, ...data } = queryFilters;
                return listaRegimesTrabalho(data);
              }}
              getSelectedEmpresa={() => empresas.find(empresa => empresa.id === Number(selectedEmpresaId))}
              dataPath="data.regimes"
            />

            <Table aria-label="Regime de Trabalho">
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Código Externo</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <SkeletonTableRow
                    numCells={5}
                    numRows={rowsPerPage < 20 ? rowsPerPage : 5}
                  />
                ) : regimes.length > 0 ? (
                    regimes.map((item) => (
                    <WorkRegimeRow
                        key={item.id}
                        data={item}
                        handleExibirAlerta={exibirAlerta}
                        handleOpenEditForm={handleEdiRegime}
                        handleUpdateStatus={updateRegimeStatus}
                        updateData={refetch}
                    />
                  ))
                ) : (
                  <EmptyTableRow colSpan={5} />
                )}
              </TableBody>
            </Table>

            <Pagination
              numTotalItems={numTotalItems}
              rowsPerPage={rowsPerPage}
              page={page}
            />
          </TableContainer>
        </Grid>
      </Grid>

      <WorkRegimeForm
        open={openForm}
        setOpen={setOpenForm}
        workRegime={selectedRegime}
        empresas={empresas}
        queryKey={queryKey}
      />
    </>
  );
}